import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FeatureType } from "../../constants/FeatureToggles";
import {
  useMaterialReactTable,
  MaterialReactTable,
} from "material-react-table";
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBInput,
} from "mdb-react-ui-kit";
import { Box, Tooltip, IconButton } from "@mui/material";
import {
  Edit as EditIcon,
  Visibility,
  VisibilityOff,
  Delete,
} from "@mui/icons-material";
import AbsModal from "../../common/absModal/AbsModal";
import { ClaimsContext } from "../../services/ClaimsService";
import "./Help.scss";
import "./Resources.scss";
import BreadCrumb from "../../common/breadCrumb/BreadCrumb";
import { HELP_DATA } from "./constants";
import { Button, COLORS, Icon, ICONS, Modal } from "abs-ui-components";
import axiosHttp from "../../services/interceptor";
import { emitToasterMsg } from "../../services/toasterUtility";
import { useSelector } from "react-redux";
import { isErSuperAdmin } from "../../utils/roleUtils";

const PRIMARY_BUTTON_COLOR = '#002A4E';

const Resources = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.userInfo.userData);
  const isSuperAdmin = useMemo(() => isErSuperAdmin(user), [user]);

  const canAdmin = isSuperAdmin;

  const [resources, setResources] = useState([]);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState([]);
  const [loading, setLoading] = useState(false);

  const [deleteResourceOpen, setDeleteResourceOpen] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState({ id: null, title: "" });
  const [modalDeleting, setModalDeleting] = useState(false);

  const [visibilityLoading, setVisibilityLoading] = useState(() => new Set());

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formTitle, setFormTitle] = useState("");
  const [formLink, setFormLink] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [saving, setSaving] = useState(false);

  /* --------------------------- Feature Toggle -------------------------- */
      const location = useLocation();
      const wcnNumber = location.state?.wcnNumber;
  
      const { userHasFeature, userFeaturesAreLoaded, fetchUserFeatures, checkWcnDelta } =
      useContext(ClaimsContext);
  
      useEffect(() => {
          if (!userFeaturesAreLoaded || checkWcnDelta(wcnNumber)) {
            fetchUserFeatures(wcnNumber);
          }
        }, [userFeaturesAreLoaded, fetchUserFeatures, checkWcnDelta, wcnNumber]);
  
      const canShowResources = useMemo(() => {
          return userHasFeature(FeatureType.ViewSupportResources);
      }, [userHasFeature]);
      const canCreateResources = useMemo(() => {
          return isSuperAdmin && userHasFeature(FeatureType.CreateSupportResource);
      }, [isSuperAdmin, userHasFeature]);
      const canEditResources = useMemo(() => {
          return isSuperAdmin && userHasFeature(FeatureType.EditSupportResource);
      }, [isSuperAdmin, userHasFeature]);
      const canToggleResources = useMemo(() => {
          return isSuperAdmin && userHasFeature(FeatureType.ToggleResourceVisibility);
      }, [isSuperAdmin, userHasFeature]);
      const canDeleteResources = useMemo(() => {
          return isSuperAdmin && userHasFeature(FeatureType.DeleteSupportResource);
      }, [isSuperAdmin, userHasFeature]);

  /* --------------------------- Derived Data -------------------------- */
  const breadcrumb = useMemo(
    () => [
      { label: HELP_DATA["nav-support-text"], url: "/support", navId: "support_navId" },
      { label: HELP_DATA["nav-help-text"], url: "/support", navId: "help_navId" },
      { label: "Resources", url: "", navId: "resources_navId" },
    ],
    []
  );

  const tableData = useMemo(
    () => resources.filter((r) => (canAdmin ? true : !r.hidden)),
    [resources, canAdmin]
  );

  const actionIconColor = "#6c757d";

  const columns = useMemo(() => {
    const cols = [
      { header: "Title", accessorKey: "title" },
      { header: "Description", accessorKey: "description" },
      {
        header: "Link",
        accessorKey: "link",
        Cell: ({ cell }) => {
          const href = cell.getValue();
          const isExternal = /^https?:\/\//i.test(href);
          return (
            <a
              href={href}
              target={isExternal ? "_blank" : "_self"}
              rel={isExternal ? "noreferrer" : undefined}
            >
              {href}
            </a>
          );
        },
      },
    ];

    if (canToggleResources || canEditResources || canDeleteResources) {
      cols.push({
        header: "Actions",
        id: "actions",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const item = row.original;
          const isHidden = !!item.hidden;
          const isToggling = visibilityLoading.has(item.id);
          return (
            <Box sx={{ display: "flex", gap: "0.5rem" }}>
              {canToggleResources && (
                <Tooltip title={isToggling ? "Updating..." : isHidden ? "Show" : "Hide"}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => !isToggling && onToggleHide(item)}
                      sx={{ color: actionIconColor, position: "relative", width: 32, height: 32 }}
                      disabled={isToggling}
                    >
                      {isToggling ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      ) : isHidden ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {canEditResources && (
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={() => onEdit(item)}
                    sx={{ color: actionIconColor }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {canDeleteResources && (
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDelete(item.id)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      });
    }
    return cols;
  }, [canToggleResources, canEditResources, canDeleteResources, visibilityLoading]);

  /* --------------------------- API Calls & Callbacks -------------------------- */
  const fetchResources = useCallback(async () => {
    try {
      setLoading(true);

      const requestBody = {
        globalFilter: search || "",
        filters: [],
        pagination: {
          page: 1,
          pageSize: 50,
        },
        sortOption: {
          SortField: sorting[0]?.id ?? "",
          IsAscending: sorting[0] ? !sorting[0].desc : false,
        }
      };

      const response = await axiosHttp.post("api/Support/GetResourceList", requestBody);

      const payload = Array.isArray(response?.data?.Data) ? response.data.Data : (Array.isArray(response?.data) ? response.data : []);
      const mapped = payload.map((r) => ({
        id: r.ResourceId,
        title: r.Title,
        description: r.Description ?? "",
        hidden: typeof r.IsViewable === "boolean" ? !r.IsViewable : false,
        link: r.LinkUrl,
      }));

      setResources(mapped);
    } catch (err) {
      console.error("Failed to load resources", err);
      setResources([]);
      emitToasterMsg("error", "Failed to load resources.");
    } finally {
      setLoading(false);
    }
  }, [search, sorting]);

  const onToggleHide = async (itemOrId) => {
    const item = typeof itemOrId === "object"
      ? itemOrId
      : resources.find((r) => r.id === itemOrId);

    if (!item?.id) return;

    const resourceId = item.id;
    const nextIsViewable = !!item.hidden ? true : false;

    try {
      setVisibilityLoading((prev) => {
        const next = new Set(prev);
        next.add(resourceId);
        return next;
      });

      setResources((prev) =>
        prev.map((r) =>
          r.id === resourceId ? { ...r, hidden: !nextIsViewable } : r
        )
      );

      const response = await axiosHttp.get("api/Support/ToggleResourceVisibility", {
        params: { resourceId, isViewable: nextIsViewable },
      });

      const updated = response?.data?.Data ?? response?.data?.data ?? response?.data;
      if (updated && typeof updated === "object") {
        setResources((prev) =>
          prev.map((r) =>
            r.id === (updated.ResourceId ?? resourceId)
              ? {
                  ...r,
                  title: updated.Title ?? r.title,
                  description: updated.Description ?? r.description,
                  hidden:
                    typeof updated.IsViewable === "boolean"
                      ? !updated.IsViewable
                      : !nextIsViewable,
                  link: updated.LinkUrl ?? r.link,
                }
              : r
          )
        );
      }
    } catch (err) {
      console.error("Failed to toggle resource visibility", err);
      emitToasterMsg("error", "Failed to update resource visibility.");
      setResources((prev) =>
        prev.map((r) =>
          r.id === resourceId ? { ...r, hidden: !!item.hidden } : r
        )
      );
    } finally {
      setVisibilityLoading((prev) => {
        const next = new Set(prev);
        next.delete(resourceId);
        return next;
      });
    }
  };

  const confirmDeleteResource = async () => {
    if (!resourceToDelete?.id) return;
    const resourceId = resourceToDelete.id;

    try {
      setModalDeleting(true);

      const response = await axiosHttp.delete("api/Support/DeleteResource", {
        params: { resourceId },
      });

      if (response?.data?.isSuccess === true) {
        setResources((prev) => prev.filter((r) => r.id !== resourceId));
        emitToasterMsg("success", "Resource deleted successfully.");
      } else {
        emitToasterMsg("error", "Failed to delete resource.");
      }
    } catch (err) {
      console.error("Failed to delete resource", err);
      emitToasterMsg("error", "Failed to delete resource.");
    } finally {
      setModalDeleting(false);
      setDeleteResourceOpen(false);
      setResourceToDelete({ id: null, title: "" });
    }
  };

  const isSafeHttpsLink = (value) => {
    if (!/^https:\/\//i.test(value)) return false;
    try {
      const url = new URL(value);
      if (url.protocol !== "https:") return false;

      const host = url.hostname.toLowerCase();
      const SAFE_SUFFIX_RE = /\.(com|org|net|edu|gov|mil)(\.[a-z]{2,3})?$/i;
      return SAFE_SUFFIX_RE.test(host);
    } catch {
      return false;
    }
  };

  const onSave = async () => {
    const title = formTitle.trim();
    const link = formLink.trim();
    const description = formDesc.trim();
    if (!title || !link) return;

    if (!isSafeHttpsLink(link)) {
      emitToasterMsg(
        "error",
        "Link must start with https:// and end with a safe domain (.com, .org, .net, .edu, .gov, .mil), including country variants like .com.eu."
      );
      return;
    }

    if (editing) {
      try {
        setSaving(true);

        const body = {
          Title: title,
          Description: description || null,
          LinkUrl: link,
        };

        const result = await axiosHttp.post("api/Support/EditResource", body, {
          params: { resourceId: editing.id },
        });

        if (result?.data?.isSuccess === false){
          emitToasterMsg("error", result?.data?.errorMessage);
          setSaving(false);
          return;
        }

        emitToasterMsg("success", "Resource updated successfully.");
        setModalOpen(false);
        resetForm();
        await fetchResources();
      } catch (err) {
        console.error("Failed to update resource", err);
        emitToasterMsg("error", "Failed to update resource.");
      } finally {
        setSaving(false);
      }
      return;
    }

    try {
      setSaving(true);
      const body = {
        Title: title,
        Description: description || null,
        LinkUrl: link,
      };

      const response = await axiosHttp.post("api/Support/CreateResource", body);

      if (response?.data?.isSuccess === false) {
        emitToasterMsg("error", response?.data?.errorMessage || "Failed to create resource.");
        setSaving(false);
        return;
      }

      const isSuccess = response?.data?.isSuccess === true || response?.status === 200;
      if (isSuccess) {
        emitToasterMsg("success", "Resource created successfully.");
        setModalOpen(false);
        resetForm();
        await fetchResources();
      } else {
        emitToasterMsg("error", "Failed to create resource.");
      }
    } catch (err) {
      console.error("Failed to create resource", err);
      emitToasterMsg("error", "Failed to create resource.");
    } finally {
      setSaving(false);
    }
  };

  /* --------------------------- Effects -------------------------- */
  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  // debounced fetch on search changes
  useEffect(() => {
    const t = setTimeout(() => fetchResources(), 300);
    return () => clearTimeout(t);
  }, [fetchResources]);

  /* --------------------------- Table Configuration -------------------------- */
  const table = useMaterialReactTable({
    columns,
    data: tableData,
    enableRowSelection: false,
    enableStickyHeader: true,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: true,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    manualFiltering: true,
    manualSorting: true,
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearch,
    initialState: {
      density: "compact",
      showGlobalFilter: true,
      columnOrder: canAdmin
        ? ["title", "description", "link", "actions"]
        : ["title", "description", "link"],
    },
    muiTableContainerProps: { sx: { maxHeight: "calc(100vh - 376px)" } },
    muiTableHeadCellProps: ({ column }) => ({
      id: `${column.columnDef.accessorKey || "actions"}`,
      sx: {
        color: "rgb(255 255 255)",
        "& .MuiButtonBase-root": {
          color: "white !important",
          "& .MuiTableSortLabel-icon": { color: "white !important" },
        },
      },
    }),
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "#042a4c !important",
        "& .MuiTableCell-head::before": { backgroundColor: "#042a4c !important" },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      className: row?.original?.hidden ? 'resource-row-hidden' : undefined,
      sx: row?.original?.hidden ? { opacity: 0.6 } : undefined,
    }),
    muiTablePaperProps: {
      sx: { boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)" },
    },
    state: {
      isLoading: loading,
      globalFilter: search,
      sorting,
    },
  });

  /* --------------------------- Handlers & Utilities -------------------------- */
  const resetForm = () => {
    setEditing(null);
    setFormTitle("");
    setFormLink("");
    setFormDesc("");
  };

  const openAdd = () => {
    resetForm();
    setModalOpen(true);
  };

  const onEdit = (item) => {
    setEditing(item);
    setFormTitle(item.title);
    setFormLink(item.link);
    setFormDesc(item.description || "");
    setModalOpen(true);
  };

  const onDelete = (id) => {
    const item = resources.find((r) => r.id === id);
    setResourceToDelete({ id, title: item?.title || "" });
    setDeleteResourceOpen(true);
  };

  const cancelDeleteResource = () => {
    setDeleteResourceOpen(false);
    setResourceToDelete({ id: null, title: "" });
  };

  /* --------------------------- JSX -------------------------- */
  if (!canShowResources) {
    return (
      <MDBContainer fluid className="px-2 px-md-3 py-4">
        <div className="mb-3">
          <BreadCrumb
            items={breadcrumb}
            onCrumbClick={(item) => {
              if (item.url) navigate(item.url);
            }}
          />
        </div>
        <MDBRow className="gx-2">
          <MDBCol md="12" lg="12">
            <MDBCard className="h-100">
              <MDBCardBody>
                <MDBCardTitle className="mb-0">Resources</MDBCardTitle>
                <div className="mt-3">You don’t have permission to view resources.</div>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>
      </MDBContainer>
    );
  }

  return (
    <MDBContainer fluid className="px-2 px-md-3 py-4">
      {/* Breadcrumb navigation */}
      <div className="mb-3">
        <BreadCrumb
          items={breadcrumb}
          onCrumbClick={(item) => {
            if (item.url) navigate(item.url);
          }}
        />
      </div>

      {/* Page content: resources card */}
      <MDBRow className="gx-2">
        <MDBCol md="12" lg="12">
          <MDBCard className="h-100">
            <MDBCardBody>
              {/* Card header: title + actions */}
              <div className="d-flex align-items-center justify-content-between">
                <MDBCardTitle className="mb-0">Resources</MDBCardTitle>
                {canCreateResources && (
                  <Button
                    onClick={openAdd}
                    startIcon={<Icon icon={ICONS.ADD} />}
                    primaryBackgroundColor={PRIMARY_BUTTON_COLOR}
                    primaryColor={PRIMARY_BUTTON_COLOR}
                  >
                    ADD RESOURCE
                  </Button>
                )}
              </div>

              {/* Table section */}
              <div className="list-height">
                <MaterialReactTable table={table} />
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>

      {/* Add/Edit Resource Modal */}
      {(canCreateResources || canEditResources) && (
        <AbsModal
          isOpen={modalOpen}
          title={editing ? "Edit resource" : "Add resource"}
          onClose={setModalOpen}
          close={() => {
            setModalOpen(false);
            resetForm();
          }}
        >
          {/* Form fields */}
          <div className="mb-3">
            <MDBInput
              label="Title"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
            />
          </div>

          <div className="mb-3">
            <MDBInput
              label="Description"
              value={formDesc}
              onChange={(e) => setFormDesc(e.target.value)}
            />
          </div>

          <div className="mb-4">
            <MDBInput
              label="Link URL"
              value={formLink}
              onChange={(e) => setFormLink(e.target.value)}
              placeholder="https://example.com"
            />
          </div>

          <div className="help-modal-footer">
            <Button
              className="save-button"
              onClick={onSave}
              disabled={saving || !formTitle.trim() || !formLink.trim()}
              primaryBackgroundColor={PRIMARY_BUTTON_COLOR}
              primaryColor={PRIMARY_BUTTON_COLOR}
            >
              <span className="d-inline-flex align-items-center">
                {saving && (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                )}
                {editing ? "Save" : "Add"}
              </span>
            </Button>
            <Button
              className="clear-button"
              onClick={() => {
                setModalOpen(false);
                resetForm();
              }}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </AbsModal>
      )}

      {/* Delete confirmation modal */}
      {canDeleteResources && (
        <div className="confirmation-modal-container">
          <Modal
            isOpen={deleteResourceOpen}
            modalHeader
            padding={"70px"}
            headerHeight={"50px"}
            showFooter
            onCloseClickListener={cancelDeleteResource}
            footer={
              <div className="d-flex justify-content-end fixed-action-btns p-4">
                <Button
                  className="modal-delete-btn"
                  onClick={confirmDeleteResource}
                  primaryColor={COLORS.RED.DARK}
                  primaryBackgroundColor={COLORS.RED.LIGHT}
                  disabled={modalDeleting}
                >
                  {modalDeleting ? (
                    <span className="d-inline-flex align-items-center">
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Deleting...
                    </span>
                  ) : (
                    "Delete"
                  )}
                </Button>
                <Button className="modal-cancel-btn" onClick={cancelDeleteResource} disabled={modalDeleting}>
                  Cancel
                </Button>
              </div>
            }
          >
            <div>
              Are you sure you want to delete this resource{resourceToDelete?.title ? `: "${resourceToDelete.title}"` : ""}?
            </div>
          </Modal>
        </div>
      )}
    </MDBContainer>
  );
};

export default Resources;